(function () {
  'use strict';

  var ATTR_METHOD = 'Liefermethode';
  var ATTR_MODUS = 'Lieferung-Modus';
  var ATTR_DATE = 'Lieferung-Datum';
  var ATTR_DATE_DISPLAY = 'Lieferung-Datum-Anzeige';
  var ATTR_ETA = 'Lieferung-ETA';
  var ATTR_ABSTELL = 'abstellgenehmigung';
  var ATTR_NACHBAR = 'nachbarzustellung';
  var ATTR_ABSTELL_ORT = 'Bevorzugter Abstellort';

  // Allow customer to pick up to 6 calendar months ahead.
  var SELECTION_WINDOW_MONTHS = 6;

  function addMonths(date, n) {
    var out = new Date(date.getTime());
    out.setMonth(out.getMonth() + n);
    return out;
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function getLogic() {
    return window.AumaerkLieferungLogic || null;
  }

  // Coalescing debouncer for cart-attribute pushes. Successive calls within
  // the window MERGE their {key:value} updates into a single payload rather
  // than dropping all but the latest. Without merging, the second pushAttr
  // call within 250ms wipes out the keys queued by the first.
  function debounceMerge(fn, ms) {
    var timer = null;
    var accum = null;
    return function (update) {
      if (!accum) accum = {};
      if (update && typeof update === 'object') {
        Object.keys(update).forEach(function (k) { accum[k] = update[k]; });
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        var payload = accum;
        accum = null;
        timer = null;
        fn(payload);
      }, ms);
    };
  }

  function postCartUpdate(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.ok ? r.json() : Promise.reject(r); });
  }

  function emitCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function Controller(root) {
    this.root = root;
    this.instance = root.getAttribute('data-cart-lieferung-instance') || 'drawer';

    this.logic = getLogic();
    if (!this.logic) {
      console.warn('AumaerkLieferung: logic module missing — picker disabled.');
      return;
    }

    this.blackoutsRaw = root.getAttribute('data-cart-lieferung-blackouts') || '';
    this.blackouts = this._computeBlackouts();

    this.asapInput = root.querySelector('[data-cart-lieferung-asap]');
    this.toggleBtn = root.querySelector('[data-cart-lieferung-toggle-picker]');
    this.calendarWrap = root.querySelector('[data-cart-lieferung-calendar]');
    this.flatpickrMount = root.querySelector('[data-cart-lieferung-flatpickr]');
    this.statusP = root.querySelector('[data-cart-lieferung-status]');
    this.abstellInput = root.querySelector('[data-cart-lieferung-abstell]');
    this.abstellLocationWrap = root.querySelector('[data-cart-lieferung-abstell-location-wrap]');
    this.abstellLocationInput = root.querySelector('[data-cart-lieferung-abstell-location]');
    this.nachbarInput = root.querySelector('[data-cart-lieferung-nachbar]');
    this.feedbackP = root.querySelector('[data-cart-lieferung-feedback]');

    this.hModus = root.querySelector('[data-cart-lieferung-hidden-modus]');
    this.hDate = root.querySelector('[data-cart-lieferung-hidden-date]');
    this.hDateDisplay = root.querySelector('[data-cart-lieferung-hidden-date-display]');
    this.hEta = root.querySelector('[data-cart-lieferung-hidden-eta]');

    this.pushAttr = debounceMerge(this._pushAttr.bind(this), 250);
    this.fp = null;
    this._pickerInitialized = false;

    this._wireEvents();
    this._recomputeEta();
    this._hydrateExtrasFromCart();
    // Commit the initial picker state (Lieferung-Modus + Lieferung-Datum +
    // Lieferung-Datum-Anzeige) to cart attributes on first mount. Without
    // this, customers who don't interact with the picker check out with
    // only Lieferung-ETA present — backend sees Modus/Datum as null and
    // cannot derive a dispatch date.
    this._pushInitialState();
    // Visibility of the picker when Abholung is active is owned entirely
    // by the `.cart--abholung [data-cart-lieferung] { display: none }`
    // CSS rule, which keys off the cart-root class managed by the
    // Abholung controller. The picker never sets `hidden` on itself.
  }

  Controller.prototype._pushInitialState = function () {
    // Commit the picker state to cart attributes on mount, BEFORE any
    // debounce window. Non-debounced + fire-and-forget so it survives a
    // "Zur Kasse" click that happens within milliseconds of cart load.
    //
    // No delivery mode is preselected: on a fresh cart Lieferung-Modus is
    // pushed empty (Shopify then drops it), and the checkout gate blocks
    // until the customer picks "Schnellstmöglich" or a specific date. A mode
    // the customer already chose in a previous interaction is preserved.
    //
    // Always-present keys: Liefermethode plus the two booleans
    // abstellgenehmigung / nachbarzustellung ("false" by default). Modus,
    // Datum, Datum-Anzeige and ETA appear only once a mode is chosen.
    var L = this.logic;
    var earliest = L.earliestDelivery(new Date(), this.blackouts);
    var etaLabel = L.formatDisplayLong(earliest);

    // No delivery mode is preselected. Only honour a mode the customer has
    // already committed (present in the rendered cart attributes); otherwise
    // stay unset so the checkout gate forces an explicit choice.
    var raw = this.hModus.value;
    var modus = raw === 'Datum' ? 'Datum' : (raw === 'ASAP' ? 'ASAP' : '');
    var datum = this.hDate.value || '';
    var datumAnzeige = this.hDateDisplay.value || '';

    var update = {};
    // Default Liefermethode to "Lieferung" if not already set. Abholung
    // toggle owns the value when the customer flips to pickup.
    update[ATTR_METHOD] = 'Lieferung';
    update[ATTR_MODUS] = modus;
    // Only surface a delivery date when a mode is actually chosen. Shopify
    // strips empty strings, so unset fields simply don't appear on the order.
    update[ATTR_ETA] = modus === 'ASAP' ? etaLabel : '';
    update[ATTR_DATE] = modus === 'Datum' ? datum : '';
    update[ATTR_DATE_DISPLAY] = modus === 'Datum' ? datumAnzeige : '';
    // Boolean attrs: explicit "false" string so backend always sees a value.
    update[ATTR_ABSTELL] = (this.abstellInput && this.abstellInput.checked) ? 'true' : 'false';
    update[ATTR_NACHBAR] = (this.nachbarInput && this.nachbarInput.checked) ? 'true' : 'false';

    // Cache the earliest ETA so choosing ASAP later can show/commit it
    // without recomputing. Don't render it while unset.
    this._etaLabel = etaLabel;
    this.hEta.value = modus === 'ASAP' ? etaLabel : '';
    this._renderStatus();

    // Fire IMMEDIATELY, not via the debounced channel. If the customer
    // checks out within the next 250ms (very real on mobile), the
    // attributes are already committed.
    this._pushAttr(update);
  };

  Controller.prototype._ensureFlatpickr = function () {
    // If we already have a Flatpickr instance bound to a still-attached
    // mount element, we're done.
    if (this.fp && this.flatpickrMount && document.contains(this.flatpickrMount)) {
      return;
    }
    // If the mount element was swapped out (cart fragment refresh) re-query
    // it from the root before (re-)initializing.
    this.flatpickrMount = this.root.querySelector('[data-cart-lieferung-flatpickr]');
    if (!this.flatpickrMount) return;
    // Wipe any leftover children from a previous failed/fallback init.
    this.flatpickrMount.innerHTML = '';
    this._pickerInitialized = true;
    this._mountFlatpickr();
  };

  Controller.prototype._computeBlackouts = function () {
    // Shared with the delivery-promise banner via lieferung-logic.js so both
    // compute identical availability. Aumaerk dispatches the day before
    // delivery → buildBlackouts blocks the day after each blackout too
    // (holiday on 04.06 → 05.06 also undeliverable).
    return this.logic.buildBlackouts(this.blackoutsRaw, new Date());
  };

  Controller.prototype._mountFlatpickr = function () {
    if (typeof window.flatpickr !== 'function') {
      console.warn('AumaerkLieferung: Flatpickr not loaded — falling back to native date input.');
      this._mountNativeFallback();
      return;
    }
    var L = this.logic;
    var blackouts = this.blackouts;
    var minDate = L.earliestDelivery(new Date(), blackouts);
    var maxDate = addMonths(L.viennaCalendarDate(new Date()), SELECTION_WINDOW_MONTHS);

    var initial = this.root.getAttribute('data-cart-lieferung-current-date') || '';
    var initialDate = L.parseISODate(initial);
    if (initialDate && !L.isSelectableDate(initialDate, blackouts, minDate)) {
      initialDate = null;
    }

    var self = this;
    this.fp = window.flatpickr(this.flatpickrMount, {
      inline: true,
      locale: 'de',
      dateFormat: 'Y-m-d',
      minDate: minDate,
      maxDate: maxDate,
      defaultDate: initialDate || null,
      disable: [
        function (date) {
          return !L.isSelectableDate(date, blackouts, minDate);
        }
      ],
      onChange: function (selected) {
        if (selected && selected.length) {
          self._onDateChosen(selected[0]);
          self._closeCalendar();
        }
      }
    });
  };

  Controller.prototype._mountNativeFallback = function () {
    var L = this.logic;
    var minDate = L.earliestDelivery(new Date(), this.blackouts);
    var input = document.createElement('input');
    input.type = 'date';
    input.min = L.toISODate(minDate);
    input.max = L.toISODate(addMonths(L.viennaCalendarDate(new Date()), SELECTION_WINDOW_MONTHS));
    var existing = this.root.getAttribute('data-cart-lieferung-current-date') || '';
    if (existing) input.value = existing;
    var self = this;
    input.addEventListener('change', function () {
      var d = L.parseISODate(input.value);
      if (d && L.isSelectableDate(d, self.blackouts, minDate)) {
        self._onDateChosen(d);
      } else {
        self._showFeedback('Bitte einen verfügbaren Werktag wählen.');
      }
    });
    this.flatpickrMount.appendChild(input);
  };

  // All interactive events are bound at document level (delegation). This
  // survives cart-drawer DOM swaps that would otherwise detach the picker's
  // elements from any handlers bound directly on them.
  //
  // Per-instance `_wireEvents` is intentionally a no-op now. State changes
  // run through `Controller.prototype.handle*` methods invoked by the
  // global delegation handlers below.
  Controller.prototype._wireEvents = function () {
    // intentionally empty — see bottom-of-file global delegation
  };

  Controller.prototype._reflectAbstellLocationVisibility = function () {
    if (!this.abstellLocationWrap || !this.abstellInput) return;
    if (this.abstellInput.checked) {
      this.abstellLocationWrap.removeAttribute('hidden');
    } else {
      this.abstellLocationWrap.setAttribute('hidden', '');
    }
  };

  // Resolve interactive refs fresh from root each call. Cheap, and prevents
  // stale references after in-place DOM swaps.
  Controller.prototype._refreshRefs = function () {
    this.calendarWrap = this.root.querySelector('[data-cart-lieferung-calendar]');
    this.toggleBtn = this.root.querySelector('[data-cart-lieferung-toggle-picker]');
    this.flatpickrMount = this.root.querySelector('[data-cart-lieferung-flatpickr]');
  };

  Controller.prototype._openCalendar = function () {
    this._refreshRefs();
    if (!this.calendarWrap || !this.toggleBtn) {
      console.warn('AumaerkLieferung: picker DOM not ready; retrying mount.');
      mountAll();
      this._refreshRefs();
      if (!this.calendarWrap || !this.toggleBtn) return;
    }
    this.calendarWrap.removeAttribute('hidden');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    this._ensureFlatpickr();
    if (this.fp && typeof this.fp.redraw === 'function') {
      try { this.fp.redraw(); } catch (_) { /* ignore */ }
    }
    var self = this;
    requestAnimationFrame(function () {
      if (!self.calendarWrap) return;
      var drawerBody = self.root.closest('.sidebar__body');
      if (drawerBody) {
        var targetTop = drawerBody.scrollTop
          + self.calendarWrap.getBoundingClientRect().top
          - drawerBody.getBoundingClientRect().top
          - 16;
        drawerBody.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
      } else if (typeof self.calendarWrap.scrollIntoView === 'function') {
        self.calendarWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  Controller.prototype._closeCalendar = function () {
    this._refreshRefs();
    if (this.calendarWrap) this.calendarWrap.setAttribute('hidden', '');
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
  };

  // modus: 'ASAP' | 'Datum' | '' (unset). '' clears the choice entirely so the
  // checkout gate re-engages.
  Controller.prototype._setModus = function (modus) {
    var update = {};
    update[ATTR_MODUS] = modus;
    if (modus !== 'Datum') {
      update[ATTR_DATE] = '';
      update[ATTR_DATE_DISPLAY] = '';
      this.hDate.value = '';
      this.hDateDisplay.value = '';
    }
    if (modus === 'ASAP') {
      update[ATTR_ETA] = this._etaLabel || '';
      this.hEta.value = this._etaLabel || '';
    } else {
      update[ATTR_ETA] = '';
      this.hEta.value = '';
    }
    this.hModus.value = modus;
    this._renderStatus();
    this.pushAttr(update);
    this._clearFeedback();
  };

  Controller.prototype._onDateChosen = function (date) {
    var L = this.logic;
    var iso = L.toISODate(date);
    var display = L.formatDisplayLong(date);
    var update = {};
    update[ATTR_MODUS] = 'Datum';
    update[ATTR_DATE] = iso;
    update[ATTR_DATE_DISPLAY] = display;
    update[ATTR_ETA] = '';
    this.hModus.value = 'Datum';
    this.hDate.value = iso;
    this.hDateDisplay.value = display;
    this.hEta.value = '';
    this.asapInput.checked = false;
    this._renderStatus();
    this.pushAttr(update);
    this._clearFeedback();
  };

  Controller.prototype._recomputeEta = function () {
    var L = this.logic;
    var earliest = L.earliestDelivery(new Date(), this.blackouts);
    var label = L.formatDisplayLong(earliest);
    if (this._etaLabel === label) return;
    this._etaLabel = label;
    // ETA is only a committed cart attribute while ASAP is the chosen mode.
    // In any other state we just cache it for later and refresh the label.
    if (this.hModus.value === 'ASAP') {
      this.hEta.value = label;
      this._renderStatus();
      var update = {};
      update[ATTR_ETA] = label;
      this.pushAttr(update);
    } else {
      this._renderStatus();
    }
  };

  Controller.prototype._renderStatus = function () {
    if (!this.statusP) return;
    var modus = this.hModus.value;
    if (modus === 'Datum' && this.hDateDisplay.value) {
      this.statusP.innerHTML = 'Gewähltes Lieferdatum: <strong></strong>';
      this.statusP.querySelector('strong').textContent = this.hDateDisplay.value;
    } else if (modus === 'ASAP' && (this._etaLabel || this.hEta.value)) {
      var eta = this._etaLabel || this.hEta.value;
      this.statusP.innerHTML = 'Lieferung am: <strong></strong>';
      this.statusP.querySelector('strong').textContent = eta;
    } else {
      this.statusP.textContent = '';
    }
  };

  Controller.prototype._hydrateExtrasFromCart = function () {
    // Server-side checked state on our own inputs already reflects cart attrs
    // (snippet reads cart.attributes['abstellgenehmigung'] etc). Sync visibility
    // of the abstell-location text input based on the checkbox state.
    this._reflectAbstellLocationVisibility();
    // Mirror state into the hidden sibling inputs in cart-subtotal.liquid so
    // legacy form submit paths see the values.
    var abstellHidden = document.querySelector('input[name="attributes[abstellgenehmigung]"]');
    var nachbarHidden = document.querySelector('input[name="attributes[nachbarzustellung]"]');
    if (abstellHidden && this.abstellInput) {
      abstellHidden.value = this.abstellInput.checked ? 'true' : 'false';
    }
    if (nachbarHidden && this.nachbarInput) {
      nachbarHidden.value = this.nachbarInput.checked ? 'true' : 'false';
    }
  };

  Controller.prototype._onCartUpdated = function (e) {
    // No-op for visibility — CSS owns that. Reserved for future cart-state
    // refresh logic (e.g. recompute ETA if blackouts changed mid-session).
    var cart = e && e.detail && e.detail.cart;
    if (!cart || !cart.attributes) return;
    var method = cart.attributes[ATTR_METHOD] || 'Lieferung';
    this.root.setAttribute('data-cart-lieferung-current-method', method);
  };

  Controller.prototype._pushAttr = function (attrs) {
    postCartUpdate(attrs).then(emitCartRefresh).catch(function (err) {
      console.warn('AumaerkLieferung: cart attribute update failed', err);
    });
  };

  Controller.prototype._showFeedback = function (msg) {
    if (!this.feedbackP) return;
    this.feedbackP.textContent = msg;
    this.feedbackP.removeAttribute('hidden');
    this.root.classList.add('cart__lieferung-picker--invalid');
  };

  Controller.prototype._clearFeedback = function () {
    if (!this.feedbackP) return;
    this.feedbackP.textContent = '';
    this.feedbackP.setAttribute('hidden', '');
    this.root.classList.remove('cart__lieferung-picker--invalid');
  };

  Controller.prototype.validate = function () {
    var modus = this.hModus.value;
    if (modus === 'ASAP') return true;
    if (modus === 'Datum' && this.hDate.value) return true;
    // Nothing chosen — block checkout, flag the picker and pull it into view
    // inside the drawer so the customer lands on the choice they have to make.
    this._showFeedback('Bitte wähle „Schnellstmöglicher Versand" oder ein Lieferdatum.');
    this._scrollToPicker();
    return false;
  };

  Controller.prototype._scrollToPicker = function () {
    var root = this.root;
    // The cart drawer scrolls inside .sidebar__body, not the window — scroll
    // that container (same approach as the Abholung checkout guard). On the
    // full cart page there's no drawer body, so fall back to scrollIntoView.
    var drawerBody = root.closest('.sidebar__body');
    if (drawerBody) {
      var targetTop = drawerBody.scrollTop
        + root.getBoundingClientRect().top
        - drawerBody.getBoundingClientRect().top
        - 16;
      drawerBody.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
    } else if (typeof root.scrollIntoView === 'function') {
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // root element → Controller instance. Map for O(1) lookup during event
  // delegation. Roots get garbage-collected once detached AND not referenced
  // here, so we explicitly prune detached entries on every mountAll() pass.
  var controllerMap = new Map();

  function listControllers() {
    var out = [];
    controllerMap.forEach(function (c, root) {
      if (document.contains(root)) out.push(c);
    });
    return out;
  }

  function controllerForRoot(root) {
    return controllerMap.get(root) || null;
  }

  function controllerForElement(el) {
    if (!el || typeof el.closest !== 'function') return null;
    var root = el.closest('[data-cart-lieferung]');
    if (!root) return null;
    return controllerForRoot(root) || ensureMountedForRoot(root);
  }

  function ensureMountedForRoot(root) {
    // Re-mount path: a fresh root from a cart fragment swap may not have
    // been picked up by mountAll() yet (e.g. user clicked before the
    // cart:refresh handler ran). Mount it on demand.
    if (controllerMap.has(root)) return controllerMap.get(root);
    var c = new Controller(root);
    controllerMap.set(root, c);
    return c;
  }

  function mountAll() {
    // Drop entries for detached roots, destroying any Flatpickr instance
    // they own to avoid leaking calendar containers + listeners on body.
    controllerMap.forEach(function (c, root) {
      if (!document.contains(root)) {
        if (c.fp && typeof c.fp.destroy === 'function') {
          try { c.fp.destroy(); } catch (_) { /* ignore */ }
        }
        controllerMap.delete(root);
      }
    });

    document.querySelectorAll('[data-cart-lieferung]').forEach(function (root) {
      if (controllerMap.has(root)) return;
      controllerMap.set(root, new Controller(root));
    });
  }

  // ---- global delegation -----------------------------------------------
  // All click/change/input/keydown handlers live here. Event targets are
  // looked up fresh on every event, so a DOM swap can never leave a stale
  // listener pointing to a detached element.

  document.addEventListener('click', function (e) {
    // Toggle the calendar.
    var toggleBtn = e.target.closest('[data-cart-lieferung-toggle-picker]');
    if (toggleBtn) {
      var c = controllerForElement(toggleBtn);
      if (!c) return;
      e.preventDefault();
      e.stopPropagation();
      if (c.calendarWrap && c.calendarWrap.hasAttribute('hidden')) {
        c._openCalendar();
      } else {
        c._closeCalendar();
      }
      return;
    }

    // Outside-click → close any open calendars.
    listControllers().forEach(function (c) {
      if (!c.calendarWrap || c.calendarWrap.hasAttribute('hidden')) return;
      if (c.calendarWrap.contains(e.target)) return;
      if (c.toggleBtn && c.toggleBtn.contains(e.target)) return;
      // Clicks inside the Flatpickr popup itself land outside calendarWrap
      // when Flatpickr appends in non-inline mode. We use inline mode here,
      // so the popup IS inside calendarWrap — no extra check needed.
      c._closeCalendar();
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    listControllers().forEach(function (c) {
      if (!c.calendarWrap || c.calendarWrap.hasAttribute('hidden')) return;
      c._closeCalendar();
      if (c.toggleBtn && typeof c.toggleBtn.focus === 'function') c.toggleBtn.focus();
    });
  });

  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target) return;

    if (target.matches('[data-cart-lieferung-asap]')) {
      var c = controllerForElement(target);
      if (!c) return;
      // Checking = choose ASAP. Unchecking returns to "nothing selected" so
      // the checkout gate re-engages; the customer picks a date via the
      // dedicated "Lieferdatum selbst wählen" button, not by unchecking.
      c._setModus(target.checked ? 'ASAP' : '');
      return;
    }

    if (target.matches('[data-cart-lieferung-abstell]')) {
      var c2 = controllerForElement(target);
      if (!c2) return;
      var checked = target.checked;
      var update = {};
      update[ATTR_ABSTELL] = checked ? 'true' : 'false';
      if (!checked) {
        if (c2.abstellLocationInput) c2.abstellLocationInput.value = '';
        update[ATTR_ABSTELL_ORT] = '';
      }
      c2._reflectAbstellLocationVisibility();
      c2.pushAttr(update);
      return;
    }

    if (target.matches('[data-cart-lieferung-nachbar]')) {
      var c3 = controllerForElement(target);
      if (!c3) return;
      var update3 = {};
      update3[ATTR_NACHBAR] = target.checked ? 'true' : 'false';
      c3.pushAttr(update3);
      return;
    }
  });

  document.addEventListener('input', function (e) {
    var target = e.target;
    if (!target) return;
    if (target.matches('[data-cart-lieferung-abstell-location]')) {
      var c = controllerForElement(target);
      if (!c) return;
      var update = {};
      update[ATTR_ABSTELL_ORT] = target.value;
      c.pushAttr(update);
    }
  });

  document.addEventListener('cart:updated', function (e) {
    listControllers().forEach(function (c) {
      c._onCartUpdated(e);
    });
  });

  function pickerIsVisuallyHidden(root) {
    // Visibility owned by CSS. We treat the picker as "hidden" for the
    // checkout-validation gate only when computed style says so — that
    // way Abholung mode (where the picker is hidden via CSS) doesn't
    // block checkout for missing Lieferung-Modus.
    if (!root || !root.isConnected) return true;
    var style = window.getComputedStyle(root);
    return style.display === 'none' || style.visibility === 'hidden';
  }

  window.AumaerkLieferung = {
    mountAll: mountAll,
    validateBeforeCheckout: function () {
      return listControllers().every(function (c) {
        if (pickerIsVisuallyHidden(c.root)) return true;
        return c.validate();
      });
    }
  };

  ready(mountAll);

  // Re-scan synchronously when the cart fragment swaps. No setTimeout — the
  // 100ms delay previously left a window in which the new toggle button had
  // no handler and a customer click was silently lost. Delegation makes the
  // re-scan idempotent and cheap.
  document.addEventListener('cart:refresh', mountAll);

  // Belt and suspenders: also observe the cart container for DOM swaps that
  // don't dispatch cart:refresh (some theme integrations / 3rd-party apps
  // replace innerHTML without firing the event).
  if (typeof MutationObserver === 'function') {
    var mountTarget = document.body;
    if (mountTarget) {
      var mo = new MutationObserver(function () {
        // Cheap guard: only re-mount when there's at least one un-mounted
        // picker root in the document.
        var roots = document.querySelectorAll('[data-cart-lieferung]');
        var needsMount = false;
        for (var i = 0; i < roots.length; i++) {
          if (!controllerMap.has(roots[i])) { needsMount = true; break; }
        }
        if (needsMount) mountAll();
      });
      mo.observe(mountTarget, { childList: true, subtree: true });
    }
  }
})();
